import { useState, useRef, useEffect, RefObject } from 'react';
import { useBell } from '@magicbell/react-headless';
import { Divider, Stack } from '@mui/material';
import Scrollbar from 'src/components/scrollbar';
import HeadlessNotification from './HeadlessNotification';
import EmptyContent from 'src/components/empty-content/EmptyContent';
import SimpleBarCore from 'simplebar-core';
import HeadlessNotificationSkeleton from './HeadlessNotificationSkeleton';

function HeadlessNotificationList() {
  const [isLoading, setIsLoading] = useState(false);

  const notificationStore = useBell({ storeId: 'inbox' });
  const scrollRef: RefObject<SimpleBarCore | HTMLDivElement | null> = useRef<
    SimpleBarCore | HTMLDivElement | null
  >(null);

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    const scrollElement =
      scrollRef.current instanceof HTMLDivElement
        ? scrollRef.current
        : scrollRef.current.getScrollElement();

    const handleScroll = async () => {
      let scrollTop = 0,
        scrollHeight = 0,
        clientHeight = 0;

      if (scrollElement) {
        scrollTop = scrollElement.scrollTop || 0;
        scrollHeight = scrollElement.scrollHeight || 0;
        clientHeight = scrollElement.clientHeight || 0;
      }

      if (
        scrollHeight - scrollTop <= clientHeight * 1.5 &&
        !isLoading &&
        notificationStore?.hasNextPage
      ) {
        setIsLoading(true);
        await notificationStore.fetchNextPage();
        setIsLoading(false);
      }
    };

    scrollElement?.addEventListener('scroll', handleScroll);
    return () => scrollElement?.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationStore?.hasNextPage, notificationStore?.fetchNextPage, scrollRef.current]);

  return (
    <>
      <Scrollbar ref={scrollRef}>
        {(notificationStore?.total || 0) > 0 && (
          <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
            {notificationStore?.notifications.map((notification) => (
              <HeadlessNotification key={notification.id} rawNotification={notification} />
            ))}
          </Stack>
        )}

        {isLoading && (
          <>
            <Divider />
            <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
              <HeadlessNotificationSkeleton />
              <HeadlessNotificationSkeleton />
              <HeadlessNotificationSkeleton />
              <HeadlessNotificationSkeleton />
              <HeadlessNotificationSkeleton />
            </Stack>
          </>
        )}

        {(notificationStore?.total || 0) == 0 && (
          <EmptyContent
            title="No Notifications"
            description="You have no notifications at this time."
          />
        )}
      </Scrollbar>
    </>
  );
}

export default HeadlessNotificationList;
